export default function WhyChooseUs() {
  const benefits = [
    {
      icon: 'ri-shield-check-line',
      title: 'HMRC Compliant',
      description: 'Fully compliant with all UK tax regulations and HMRC requirements, ensuring your business stays on the right side of the law.'
    },
    {
      icon: 'ri-user-star-line',
      title: 'Expert Team',
      description: 'Qualified accountants with extensive experience in UK tax law and accounting standards, dedicated to your success.'
    },
    {
      icon: 'ri-time-line',
      title: 'Time-Saving',
      description: 'Free up your valuable time to focus on growing your business while we handle all your accounting and tax needs.'
    },
    {
      icon: 'ri-money-pound-circle-line',
      title: 'Cost-Effective',
      description: 'Competitive pricing with transparent fees and no hidden costs. Save money through strategic tax planning.'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Dedicated Support',
      description: 'Personal account manager and responsive support team available to answer your questions whenever you need us.'
    },
    {
      icon: 'ri-cloud-line',
      title: 'Cloud-Based',
      description: 'Modern cloud accounting solutions with real-time access to your financial data from anywhere, anytime.'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Mitchell',
      role: 'Small Business Owner',
      company: 'Mitchell Consulting Ltd',
      image: 'https://public.readdy.ai/ai/img_res/46a8ee2bd9eb94afd19d2340fdfeb4d0.jpg',
      rating: 5,
      text: 'Switching to AccountPro UK was the best decision for my business. They handle everything from bookkeeping to tax returns with such professionalism. I can finally focus on what I do best!'
    },
    {
      name: 'James Thompson',
      role: 'Freelance Consultant',
      company: 'Self-Employed',
      image: 'https://readdy.ai/api/search-image?query=Professional%20British%20businessman%20in%20his%20thirties%20with%20warm%20approachable%20smile%20wearing%20business%20casual%20attire%20in%20contemporary%20office%20environment%20with%20soft%20natural%20lighting%20confident%20professional%20demeanor%20high%20quality%20portrait%20photography&width=400&height=400&seq=testimonial-002&orientation=squarish',
      rating: 5,
      text: 'As a self-employed consultant, I was overwhelmed with tax paperwork. The team made everything simple and saved me a significant amount on my tax bill through smart planning.'
    },
    {
      name: 'Emma Roberts',
      role: 'Managing Director',
      company: 'Roberts & Associates',
      image: 'https://readdy.ai/api/search-image?query=Professional%20British%20businesswoman%20in%20her%20fifties%20with%20experienced%20confident%20expression%20wearing%20elegant%20business%20suit%20in%20upscale%20modern%20office%20with%20sophisticated%20lighting%20professional%20executive%20presence%20high%20quality%20portrait%20photography&width=400&height=400&seq=testimonial-003&orientation=squarish',
      rating: 5,
      text: 'Outstanding service! Their expertise in VAT and payroll has been invaluable. The monthly management accounts help us make better business decisions. Highly recommended!'
    }
  ];

  return (
    <section id="why-us" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-blue-100 rounded-full px-4 py-2 mb-4">
            <i className="ri-star-line text-blue-600"></i>
            <span className="text-blue-600 text-sm font-semibold">Why Choose Us</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Your Trusted Financial Partner
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We combine expertise, technology, and personalized service to deliver exceptional results for your business
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {benefits.map((benefit, index) => (
            <div 
              key={index}
              className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-8 hover:shadow-lg transition-all border border-gray-100"
            >
              <div className="w-14 h-14 bg-blue-600 rounded-lg flex items-center justify-center mb-6">
                <i className={`${benefit.icon} text-3xl text-white`}></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {benefit.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>

        <div className="mb-12">
          <h3 className="text-3xl font-bold text-gray-900 text-center mb-12">
            What Our Clients Say
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
              >
                <div className="flex gap-1 mb-6">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-xl"></i>
                  ))}
                </div>
                
                <p className="text-gray-700 mb-6 leading-relaxed italic">
                  "{testimonial.text}"
                </p>

                <div className="flex items-center gap-4">
                  <img 
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-14 h-14 rounded-full object-cover object-top"
                  />
                  <div>
                    <div className="font-bold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.role}</div>
                    <div className="text-sm text-blue-600">{testimonial.company}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-12 text-center">
          <div className="max-w-3xl mx-auto">
            <h3 className="text-3xl font-bold text-white mb-4">
              Ready to Transform Your Business Finances?
            </h3>
            <p className="text-gray-300 text-lg mb-8">
              Join hundreds of satisfied UK businesses who trust us with their accounting and tax needs. Get started with a free consultation today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => {
                  const element = document.getElementById('contact');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-blue-600 text-white px-8 py-4 rounded-lg hover:bg-blue-700 transition-all shadow-lg inline-flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap text-lg font-semibold"
              >
                Get Started Now
                <i className="ri-arrow-right-line text-xl"></i>
              </button>
              <button 
                onClick={() => {
                  const widget = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
                  if (widget) widget.click();
                }}
                className="bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 px-8 py-4 rounded-lg hover:bg-white/20 transition-all inline-flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap text-lg font-semibold"
              >
                <i className="ri-phone-line text-xl"></i>
                Talk to an Expert
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
